import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Stack, Avatar, TextField, 
  List, ListItemButton, ListItemText, IconButton, Container, Button, 
  CircularProgress, Badge, Divider, useMediaQuery, useTheme
} from "@mui/material";
import { 
  SmartToy, Person, Search, MoreVert, Security, Send, 
  PanTool, Refresh, InfoOutlined, Forum, ArrowBackIosNew 
} from "@mui/icons-material";

const AIQuestions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  // États
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // Nouvel état pour la recherche
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showChatMobile, setShowChatMobile] = useState(false);

  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  useEffect(() => { scrollToBottom(); }, [selectedSession?.messages, loading]);

  // Filtrage des sessions par ID ou par le texte du dernier message
  const filteredSessions = sessions.filter((s) => 
    s.session_id.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (s.last_message && s.last_message.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const fetchSessions = useCallback(async (isInitial = false) => {
    try {
      const response = await axios.get(`${apiUrl}/admin/ai/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data || []);
      if (!isMobile && isInitial && response.data.length > 0 && !selectedSession) {
        handleSelectSession(response.data[0].session_id);
      }
    } catch (err) { console.error("Erreur sessions", err); }
  }, [apiUrl, token, selectedSession, isMobile]);

  const handleSelectSession = async (sessionId) => {
    if (isMobile) setShowChatMobile(true);
    try {
      await axios.post(`${apiUrl}/admin/ai/sessions/${sessionId}/mark-as-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(prev => prev.map(s => s.session_id === sessionId ? { ...s, unread_count: 0 } : s));
      fetchOneConversation(sessionId);
    } catch (err) { fetchOneConversation(sessionId); }
  };

  const fetchOneConversation = useCallback(async (sessionId) => {
    setLoading(true);
    setIsReplying(false);
    try {
      const response = await axios.get(`${apiUrl}/admin/ai/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [apiUrl, token]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedSession) return;
    try {
      const response = await axios.post(`${apiUrl}/admin/ai/sessions/${selectedSession.session_id}/reply`, 
        { message: replyText }, { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedSession(prev => ({ ...prev, messages: [...prev.messages, response.data.new_message] }));
      setReplyText("");
      setIsReplying(false);
      fetchSessions();
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchSessions(true);
    const interval = setInterval(() => fetchSessions(false), 10000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  return (
    <Box sx={{ bgcolor: "#F0F2F5", minHeight: "100vh", p: { xs: 1, md: 3 } }}>
      <Container maxWidth="xl">
        
        {/* HEADER DE PAGE (Masqué sur mobile si chat ouvert) */}
        {(!isMobile || !showChatMobile) && (
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: "#1877F2", width: { xs: 40, md: 50 }, height: { xs: 40, md: 50 } }}>
                <Forum sx={{ fontSize: { xs: 24, md: 30 } }} />
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "h6" : "h4"} fontWeight="800">Supervision IA</Typography>
                <Typography variant="caption" color="text.secondary">Gérez les échanges clients en temps réel.</Typography>
              </Box>
            </Stack>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {/* ZONE PRINCIPALE */}
        <Box sx={{ display: "flex", gap: 2, height: { xs: "calc(100vh - 140px)", md: "75vh" } }}>
          
          {/* SIDEBAR : LISTE DES CLIENTS */}
          {(!isMobile || !showChatMobile) && (
            <Paper sx={{ 
              width: { xs: "100%", md: 380 }, 
              borderRadius: 3, 
              display: "flex", 
              flexDirection: "column", 
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              overflow: "hidden"
            }}>
              <Box sx={{ p: 2, borderBottom: "1px solid #EEE" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="800">Files d'attente</Typography>
                  <IconButton onClick={() => fetchSessions(false)} size="small"><Refresh /></IconButton>
                </Stack>
                
                {/* FILTRE DE RECHERCHE */}
                <TextField 
                  fullWidth 
                  size="small" 
                  placeholder="Chercher client ou message..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{ 
                    startAdornment: <Search sx={{ mr: 1, color: "grey.500", fontSize: 20 }} />,
                    sx: { borderRadius: 10, bgcolor: "#F0F2F5" } 
                  }} 
                />
              </Box>

              <List sx={{ flex: 1, overflowY: "auto", p: 1 }}>
                {filteredSessions.length > 0 ? (
                  filteredSessions.map((s) => (
                    <ListItemButton 
                      key={s.session_id}
                      selected={selectedSession?.session_id === s.session_id}
                      onClick={() => handleSelectSession(s.session_id)}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <Badge badgeContent={s.unread_count} color="error" invisible={s.unread_count === 0}>
                        <Avatar sx={{ bgcolor: "#1877F2" }}><Person /></Avatar>
                      </Badge>
                      <ListItemText 
                        sx={{ ml: 2 }}
                        primary={
                          <Stack direction="row" justifyContent="space-between">
                             <Typography variant="subtitle2" noWrap sx={{ maxWidth: '120px' }}>{s.session_id.substring(0, 12)}</Typography>
                             <Typography variant="caption" color="text.secondary">
                                {new Date(s.last_activity).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                             </Typography>
                          </Stack>
                        }
                        secondary={<Typography variant="caption" noWrap display="block" color={s.unread_count > 0 ? "primary.main" : "text.secondary"} sx={{ fontWeight: s.unread_count > 0 ? 700 : 400 }}>{s.last_message}</Typography>}
                      />
                    </ListItemButton>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    Aucun résultat trouvé
                  </Typography>
                )}
              </List>
            </Paper>
          )}

          {/* ZONE DE CHAT */}
          {(!isMobile || showChatMobile) && (
            <Paper sx={{ 
              flex: 1, 
              borderRadius: 3, 
              display: "flex", 
              flexDirection: "column", 
              bgcolor: "#FFF", 
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              overflow: "hidden"
            }}>
              {selectedSession ? (
                <>
                  {/* Header Chat */}
                  <Box sx={{ p: 2, borderBottom: "1px solid #EEE", display: "flex", alignItems: "center", gap: 1 }}>
                    {isMobile && (
                      <IconButton onClick={() => setShowChatMobile(false)} sx={{ mr: 1 }}>
                        <ArrowBackIosNew />
                      </IconButton>
                    )}
                    <Avatar sx={{ bgcolor: "#E7F3FF", color: "#1877F2", width: 35, height: 35 }}><SmartToy /></Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight="bold" variant="body2" noWrap>{selectedSession.session_id}</Typography>
                      <Typography variant="caption" color="success.main">● IA Agent Actif</Typography>
                    </Box>
                    <IconButton size="small"><MoreVert /></IconButton>
                  </Box>

                  {/* Bandeau Info */}
                  <Box sx={{ bgcolor: "#FFF9C4", p: 1, px: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <InfoOutlined sx={{ color: "#FBC02D", fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontWeight: '500' }}>Mode Supervision : Prenez la main pour répondre en direct.</Typography>
                  </Box>

                  {/* Messages */}
                  <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 }, bgcolor: "#FAFAFA" }}>
                    {loading ? <CircularProgress sx={{ m: "auto", display: "block" }} /> : (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 3, color: 'text.secondary', fontWeight: 'bold' }}>
                          {selectedSession.date_full}
                        </Typography>
                        {selectedSession.messages.map((msg) => (
                          <Box key={msg.id} sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: msg.type === 'user' ? "flex-start" : "flex-end" }}>
                            <Stack direction={msg.type === 'user' ? "row" : "row-reverse"} spacing={1} alignItems="flex-end">
                              <Paper sx={{ p: 1.5, borderRadius: 3, bgcolor: msg.type === 'user' ? "#FFF" : "#1877F2", color: msg.type === 'user' ? "#000" : "#FFF", maxWidth: "85%", border: msg.type === 'user' ? "1px solid #E4E6EB" : "none" }}>
                                <Typography variant="body2">{msg.text}</Typography>
                              </Paper>
                              <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.secondary" }}>{msg.time}</Typography>
                            </Stack>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>

                  {/* Zone de saisie */}
                  <Box sx={{ p: 2, borderTop: "1px solid #EEE" }}>
                    {!isReplying ? (
                      <Button fullWidth variant="contained" color="error" startIcon={<PanTool />} onClick={() => setIsReplying(true)} sx={{ textTransform: "none", borderRadius: 2 }}>Interrompre l'IA et répondre</Button>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <TextField fullWidth autoFocus size="small" placeholder="Message..." value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendReply()} />
                        <IconButton color="primary" onClick={handleSendReply} disabled={!replyText.trim()}><Send /></IconButton>
                        <Button onClick={() => setIsReplying(false)}>X</Button>
                      </Stack>
                    )}
                  </Box>
                </>
              ) : (
                <Box sx={{ m: "auto", textAlign: "center", p: 3 }}>
                  <Security sx={{ fontSize: 70, color: "#EEE", mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">Sélectionnez une conversation</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AIQuestions;